import { isEmpty } from 'lodash/fp';
import { VFC } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import { SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import FormSection from 'dashboard/layouts/FormLayout/Section';
import { Container, Header, Section as IndexSection } from 'dashboard/layouts/IndexViewLayoutV2';
import { Environment, EnvironmentQuotaAction } from 'dashboard/models/environment';
import Card from 'design-system/components/Card';

const API_QUOTA_ACTION_DISPLAY_NAME: Record<EnvironmentQuotaAction, string> = {
  [EnvironmentQuotaAction.CreateCase]: 'Case creation',
  [EnvironmentQuotaAction.CreateInquiryPerAccount]: 'Inquiry creation per account',
  [EnvironmentQuotaAction.CreateInquiry]: 'Inquiry creation',
  // [EnvironmentQuotaAction.CreateReportSync]: 'Sync report creation',
  [EnvironmentQuotaAction.CreateReport]: 'Report creation',
  // [EnvironmentQuotaAction.CreateSentinelTransaction]: 'Sentinel transaction creation',
  [EnvironmentQuotaAction.CreateTransaction]: 'Transaction creation',
  [EnvironmentQuotaAction.CreateVerification]: 'Verification creation',
  [EnvironmentQuotaAction.CreateWorkflowRun]: 'Workflow Run creation',
  [EnvironmentQuotaAction.SendEmail]: 'Send email',
};

const SectionDescription = styled('div')`
  margin-bottom: ${SPACINGS.base * 3}px;
`;

interface DashboardApiRateLimitsViewProps {
  currentEnvironment: Environment;
}

export const DashboardApiRateLimitsView: VFC<DashboardApiRateLimitsViewProps> = ({
  currentEnvironment,
}) => {
  const { quotas, 'rate-limit': rateLimit } = currentEnvironment.attributes;

  const rateLimitDisplay = isEmpty(rateLimit)
    ? 'None'
    : /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      `${rateLimit.limit} per ${rateLimit['period-seconds']} seconds`;

  return (
    <>
      <InactiveEnvironmentBanner />
      <Container>
        <Header title='Rate Limits' data-test='api-rate-limits__header' />
        <IndexSection>
          <Card>
            <Card.Body>
              <SectionDescription>
                <p>
                  Read more about API rate limits{' '}
                  <a
                    href='https://docs.withpersona.com/reference/rate-limiting'
                    target='_blank'
                    rel='noreferrer'
                  >
                    here
                  </a>
                  . If you need your rate limit changed, please{' '}
                  <Link to='/dashboard/contact-us'>contact us</Link>.
                </p>
              </SectionDescription>
              <FormSection cols={2} colsMobile={1}>
                <Card>
                  <Card.Body>
                    <Card.Title>API requests</Card.Title>
                    <p>{rateLimitDisplay}</p>
                  </Card.Body>
                </Card>
              </FormSection>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <Card.Title>Quotas</Card.Title>
              <SectionDescription>
                <p>
                  If you need your quotas changed, please{' '}
                  <Link to='/dashboard/contact-us'>contact us</Link>.
                </p>
              </SectionDescription>
              <FormSection cols={2} colsMobile={1}>
                {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                {quotas.map((quota) => {
                  const actionName = API_QUOTA_ACTION_DISPLAY_NAME[quota.action];

                  if (!actionName) {
                    return;
                  }

                  return (
                    <Card key={`${currentEnvironment.id}-${quota.action}`}>
                      <Card.Body>
                        <Card.Title>{actionName}</Card.Title>
                        <p>{`${quota.limit} per ${quota.period}`}</p>
                      </Card.Body>
                    </Card>
                  );
                })}
              </FormSection>
            </Card.Body>
          </Card>
        </IndexSection>
      </Container>
    </>
  );
};

export default DashboardApiRateLimitsView;
